import { Download, Facebook, Twitter, Linkedin, Instagram, ChevronLeft, ChevronRight } from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import { useState } from 'react'
import Breadcrumb from '@/components/ui/Breadcrumb'
import Newsletter from '@/components/sections/Newsletter'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { useTender, useTenders } from '@/lib/api/hooks/tender'
import type { Tender } from '@/lib/api/types'
import { motion } from 'framer-motion'
import { fadeUp, staggerContainer, viewportReveal } from '@/lib/motion'
import { parseHtmlString } from '@/utils/helpers/html'

// Helper function to format bid type from API to display format
const formatBidType = (bidType: string): string => {
  const bidTypeMap: Record<string, string> = {
    'OPEN_BID': 'Open Tender',
    'RESTRICTED_BID': 'Restricted Tender',
    'REQUEST_FOR_PROPOSAL': 'Request for Proposal',
    'REQUEST_FOR_QUOTATION': 'Request for Quotation'
  }
  return bidTypeMap[bidType] || bidType
}

// Helper function to format date
const formatDate = (dateString: string): string => {
  const date = new Date(dateString)
  const months = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December']
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`
}


const formatDateRange = (startDate: string, endDate: string): string => {
  return `${formatDate(startDate)} to ${formatDate(endDate)}`
}

const ProcurementDetail = () => {
  const navigate = useNavigate()
  const { tenderId } = useParams<{ tenderId: string }>()

  const { data: tenderData, isLoading, error } = useTender(tenderId)


  const { data: otherTendersData } = useTenders({ page: 1, size: 10 }, {})

  const apiTender = tenderData?.data
  const allTenders = otherTendersData?.data?.data || []

  const currentTenderIndex = allTenders.findIndex((t: Tender) => t.tenderId === tenderId)
  const hasPrevious = currentTenderIndex > 0
  const hasNext = currentTenderIndex >= 0 && currentTenderIndex < allTenders.length - 1
  const previousTender = hasPrevious ? allTenders[currentTenderIndex - 1] : null
  const nextTender = hasNext ? allTenders[currentTenderIndex + 1] : null

  const otherTenders = allTenders
    .filter((t: Tender) => t.tenderId !== tenderId)
    .slice(0, 3)

  const handleShare = (platform: string) => {
    if (!apiTender) return

    const url = encodeURIComponent(window.location.href)
    const title = encodeURIComponent(`${apiTender.tenderTitle} - African Space Agency`)

    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank')
        break
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${url}&text=${title}`, '_blank')
        break
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank')
        break
      case 'instagram':
        navigator.clipboard.writeText(window.location.href)
        alert('Link copied to clipboard! You can share this on Instagram.')
        break
      default:
        navigator.clipboard.writeText(window.location.href)
    }
  }

  const [isDownloadingTender, setIsDownloadingTender] = useState(false)

  const handleDownloadTender = async () => {
    if (!apiTender?.tenderUrl) {
      alert('Tender document is not available for download.')
      return
    }

    setIsDownloadingTender(true)
    try {
      const { downloadPdf } = await import('../../../../utils/download')
      await downloadPdf(apiTender.tenderUrl, apiTender.tenderTitle)
    } catch (error) {
      console.error('Download error:', error)
      window.open(apiTender.tenderUrl, '_blank', 'noopener,noreferrer')
    } finally {
      setIsDownloadingTender(false)
    }
  }

  const handleSubmitBid = () => {
    // TODO: Implement the logic to navigate to the bid submission page or open a modal for bid submission.
  }

  if (isLoading) {
    return <LoadingSpinner fullScreen message="Loading tender details..." />
  }

  if (error || !apiTender) {
    return (
      <div className="bg-white min-h-screen">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="text-center py-12">
            <p className="text-red-600 mb-4">Tender not found or error loading tender.</p>
            <button
              onClick={() => navigate('/opportunities')}
              className="px-4 py-2 bg-wine-100 text-white rounded hover:bg-wine-200 transition-colors"
            >
              Back to Opportunities
            </button>
          </div>
        </div>
      </div>
    )
  }

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Procurement', href: '/opportunities' },
    { label: apiTender.tenderTitle, href: '#', active: true }
  ]

  return (
    <motion.div
      className="bg-white min-h-screen"
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
    >
      <motion.div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 pb-16" variants={fadeUp}>
        <div className="mb-5">
          <Breadcrumb items={breadcrumbItems} />
        </div>

        <motion.div
          className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-14"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewportReveal}
        >
          <div className="lg:col-span-2 order-1">
            <div className="space-y-12">
              <div className="space-y-6">
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-normal font-pt-serif text-gray-900 leading-tight">
                  {apiTender.tenderTitle}
                </h1>

                <div className="flex flex-col sm:flex-row gap-6">
                  <div className="flex items-start gap-1">
                    <span className="text-base font-bold font-satoshi text-wine-100">Deadline</span>
                    <span className="text-base font-normal font-satoshi text-gray-600"> - {formatDate(apiTender.endDate)}</span>
                  </div>
                  <div className="flex items-start gap-1">
                    <span className="text-base font-bold font-satoshi text-wine-100">Bid Type</span>
                    <span className="text-base font-normal font-satoshi text-gray-600"> - {formatBidType(apiTender.bidType)}</span>
                  </div>
                  <div className="flex items-start gap-1">
                    <span className="text-base font-bold font-satoshi text-wine-100">Date Range</span>
                    <span className="text-base font-normal font-satoshi text-gray-600"> - {formatDateRange(apiTender.startDate, apiTender.endDate)}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                {apiTender.description && (
                  <p className="text-lg font-normal font-satoshi text-gray-900 leading-relaxed">
                    <span dangerouslySetInnerHTML={parseHtmlString(apiTender.description)} />
                  </p>
                )}
              </div>

              <div className="space-y-8">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-6">
                  <div className="w-6 h-6 bg-orange-500 rounded-md flex items-center justify-center">
                    <div className="w-4 h-4 bg-white rounded-sm"></div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4">
                    {apiTender.tenderUrl && (
                      <button
                        onClick={handleDownloadTender}
                        disabled={isDownloadingTender}
                        className="px-4 py-2.5 border border-gray-300 rounded flex items-center gap-2 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Download className="w-4 h-4" />
                        <span className="text-base font-medium font-satoshi text-gray-900">
                          {isDownloadingTender ? 'Downloading...' : 'Download Tender'}
                        </span>
                      </button>
                    )}

                    <button
                      onClick={handleSubmitBid}
                      className="px-8 py-2.5 bg-wine-100 rounded flex items-center justify-center hover:bg-wine-200 transition-colors"
                    >
                      <span className="text-base font-bold font-satoshi text-white">
                        Submit Bid
                      </span>
                    </button>
                  </div>
                </div>

                <div className="space-y-4">
                  <span className="text-lg font-normal font-pt-serif text-gray-600">
                    SHARE
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleShare('facebook')}
                      className="w-6 h-6 text-gray-800 hover:text-forestGreen-100 transition-colors"
                      aria-label="Share on Facebook"
                    >
                      <Facebook className="w-full h-full" />
                    </button>
                    <button
                      onClick={() => handleShare('twitter')}
                      className="w-6 h-6 text-gray-800 hover:text-forestGreen-100 transition-colors"
                      aria-label="Share on Twitter"
                    >
                      <Twitter className="w-full h-full" />
                    </button>
                    <button
                      onClick={() => handleShare('linkedin')}
                      className="w-6 h-6 text-gray-800 hover:text-forestGreen-100 transition-colors"
                      aria-label="Share on LinkedIn"
                    >
                      <Linkedin className="w-full h-full" />
                    </button>
                    <button
                      onClick={() => handleShare('instagram')}
                      className="w-6 h-6 text-gray-800 hover:text-pink-600 transition-colors"
                      aria-label="Share on Instagram"
                    >
                      <Instagram className="w-full h-full" />
                    </button>
                  </div>
                </div>
              </div>

              {(hasPrevious || hasNext) && (
                <div className="flex items-center justify-between gap-4 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => previousTender && navigate(`/opportunities/${previousTender.tenderId}`)}
                    disabled={!hasPrevious}
                    className={`flex items-center gap-2 px-4 py-2 rounded transition-colors ${hasPrevious
                      ? 'text-gray-900 hover:bg-gray-200'
                      : 'text-gray-400 cursor-not-allowed'
                      }`}
                  >
                    <ChevronLeft className="w-5 h-5" />
                    <span className="text-sm font-medium font-satoshi">Previous</span>
                  </button>

                  <button
                    onClick={() => nextTender && navigate(`/opportunities/${nextTender.tenderId}`)}
                    disabled={!hasNext}
                    className={`flex items-center gap-2 px-4 py-2 rounded transition-colors ${hasNext
                      ? 'text-gray-900 hover:bg-gray-200'
                      : 'text-gray-400 cursor-not-allowed'
                      }`}
                  >
                    <span className="text-sm font-medium font-satoshi">Next</span>
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-1 order-2">
            <div className="space-y-10">
              <div className="space-y-6">
                <h3 className="text-xl sm:text-2xl font-normal font-pt-serif text-gray-900 leading-7">
                  OTHER TENDERS
                </h3>

                <div className="space-y-4">
                  {otherTenders.length > 0 ? (
                    otherTenders.map((tender: Tender) => (
                      <div
                        key={tender.tenderId}
                        onClick={() => navigate(`/opportunities/${tender.tenderId}`)}
                        className="block px-4 py-6 bg-gray-50 rounded-md hover:bg-gray-200 transition-colors cursor-pointer"
                      >
                        <div className="space-y-4">
                          <p className="text-base font-medium font-satoshi text-gray-900 leading-normal">
                            {tender.tenderTitle}
                          </p>
                          <p className="text-base font-normal font-satoshi text-gray-900">
                            {formatDateRange(tender.startDate, tender.endDate)}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-600 text-sm">No other tenders available.</p>
                  )}
                </div>
              </div>


              {/* Newsletter */}
              <Newsletter />
            </div>
          </div>
        </motion.div>
      </motion.div>
    </motion.div>
  )
}

export default ProcurementDetail